import React, { useState, useEffect, useContext } from 'react';
import classNames from 'classnames';
import DOMPurify from "isomorphic-dompurify";
import parse from 'html-react-parser';
import { graphql, navigate } from 'gatsby';

import Link from '../Link';
import Image from '../Image';
import Button from '../Button';
import QuickShop from '../QuickShop';
import { AuthContext } from '../../context/auth-context';
import { convertToElevarDataItem, formatPrice } from '../../utils';

const ns = `product-card`;

const ProductCard = ({
	location,
	category,
	product,
	index,
	hideLearnMore = true,
	hideQuickshop = false,
	hidePrice = false,
	hideTag = false,
	hideRating = false,
	campaignCopy = '',
	hasActiveCampaign,
	activeCampaign,
}) => {
	const rootClassnames = classNames({
		[`${ ns }`]: true,
	});

	const [quickShopOpen, setQuickShopOpen] = useState(false);
	const [newProductFlag, setNewProductFlag] = useState(null);
	const { state: authState } = useContext(AuthContext);
	const { auth = {}, userProperties, userPropertiesLoaded } = authState;

	const sanitizedCampaignCopy = DOMPurify.sanitize(campaignCopy);
	const parsedCampaignCopy = parse(sanitizedCampaignCopy);

	const {
		title,
		priceRangeV2,
		priceRange,
		slug,
		handle,
		images,
		tags,
	} = product;

	let firstImage;
	switch (true) {
		case Array.isArray(images?.edges):
			firstImage = images.edges[0]?.node;
			break;
		case Array.isArray(images):
			firstImage = images[0]?.image || images[0];
			break;
		default:
			//	Do not assign a value in this case
	}

	const price = formatPrice(
		priceRangeV2?.minVariantPrice?.currencyCode || priceRange?.minVariantPrice?.currencyCode,
		priceRangeV2?.minVariantPrice?.amount || priceRange?.minVariantPrice?.amount
	);

	useEffect(() => {
		if (product.tags.includes('New')) {
			setNewProductFlag(true);
		}
	}, [product]);

	// Todo: Add rating when review service is available
	const starsPlaceholder = () => {
		return (
			<svg width={'66'} height={'12'} viewBox={'0 0 66 12'} fill={'none'} xmlns={'http://www.w3.org/2000/svg'}>
				<path d={'M6.66797 0L8.01505 4.1459H12.3743L8.8476 6.7082L10.1947 10.8541L6.66797 8.2918L3.14126 10.8541L4.48834 6.7082L0.96163 4.1459H5.32088L6.66797 0Z'} fill={'#FFCB05'} />
				<path d={'M20.001 0L21.3481 4.1459H25.7073L22.1806 6.7082L23.5277 10.8541L20.001 8.2918L16.4743 10.8541L17.8213 6.7082L14.2946 4.1459H18.6539L20.001 0Z'} fill={'#FFCB05'} />
				<path d={'M33.332 0L34.6791 4.1459H39.0384L35.5117 6.7082L36.8587 10.8541L33.332 8.2918L29.8053 10.8541L31.1524 6.7082L27.6257 4.1459H31.9849L33.332 0Z'} fill={'#FFCB05'} />
				<path d={'M46.667 0L48.0141 4.1459H52.3733L48.8466 6.7082L50.1937 10.8541L46.667 8.2918L43.1403 10.8541L44.4874 6.7082L40.9607 4.1459H45.3199L46.667 0Z'} fill={'#FFCB05'} />
				<path d={'M60 0L61.3471 4.1459H65.7063L62.1796 6.7082L63.5267 10.8541L60 8.2918L56.4733 10.8541L57.8204 6.7082L54.2937 4.1459H58.6529L60 0Z'} fill={'#FFCB05'} />
			</svg>
		);
	};

	return (
		<>
			<Link
				className={`${ rootClassnames }`}
				to={slug || `/products/nordic-naturals/${ handle }`}
				aria-label={`View ${ title } product page`}
				state={{
					prevPath: location?.pathname?.includes('search') ? 'search results' : location?.pathname || '',
					prevCategory: category,
				}}
				onClick={() => {
					if (userPropertiesLoaded) {
						window.ElevarDataLayer = window.ElevarDataLayer ?? [];
						window.ElevarDataLayer.push({
							event: 'dl_select_item',
							user_properties: userProperties,
							ecommerce: {
								currencyCode: 'USD',
								click: {
									actionField: {
										list: location.pathname,
										action: 'click',
									},
									products: [
										convertToElevarDataItem({ item: product, index, location }),
									],
								},
							},
						});
					}
				}}
			>
				{newProductFlag && <div className="new-product-flag">NEW</div>}
				<div className={`${ ns }__image`}>
					<div className={`${ ns }__image-1`}>
						<Image sourceUrl={firstImage?.src || firstImage?.originalSrc} altText={firstImage?.altText || `Product Image ${ title }`} dataMedia />
					</div>
					{!hideTag && tags.length > 0 && tags.map(tag => {
						if (tag.includes('Format') && tag.includes('::')) {
							return (
								<div className={`${ ns }__tag eyebrow`} key={tag}>
									{tag.split('::').pop()}
								</div>
							);
						}
						return null;
					})}
					{!hideQuickshop && hideLearnMore && (
						<div className={`${ ns }__quickshop`}>
							<Button
								onClick={e => {
									e.preventDefault();
									setQuickShopOpen(true);
								}}
							>
								Quick Shop
							</Button>
						</div>
					)}
					{hideQuickshop && !hideLearnMore && (
						<div className={`${ ns }__learn-more`}>
							<Button
								onClick={() => {
									navigate(slug || `/products/nordic-naturals/${ handle }`, {
										state: {
											prevPath: location?.pathname?.includes('search') ? 'search results' : location?.pathname || '',
										}
									});
								}}
							>
								Learn More
							</Button>
						</div>
					)}
				</div>
				<div className={classNames({
					[`${ ns }__info`]: true,
					[`${ ns }__info--no-button`]: hideQuickshop === hideLearnMore,
					})}
				>
					<div className={`${ ns }__title`}>
						{title}
					</div>
					{!hidePrice && auth.isAuthorized && (
						<div className={`${ ns }__price`}>
							{`From ${ price }`}
						</div>
					)}
					{!hideRating && hidePrice && auth.isAuthorized && (
						<div className={`${ ns }__rating`}>
							{starsPlaceholder()}
						</div>
					)}
				</div>
				{(hasActiveCampaign && activeCampaign && campaignCopy) && (
					<div className={`${ ns }__campaign`}>
						{parsedCampaignCopy}
					</div>
				)}
			</Link>
			{quickShopOpen && !hideQuickshop && (
				<QuickShop
					location={location}
					product={product}
					setQuickShopOpen={setQuickShopOpen}
					quickShopOpen
				/>
			)}
		</>
	);
};

export default ProductCard;

export const query = graphql`
  fragment ProductCard on ShopifyProduct {
		shopifyId
    id
    title
    slug: gatsbyPath(
      filePath: "/products/{ShopifyProduct.productType}/{ShopifyProduct.handle}"
    )
		images: media {
			... on ShopifyMediaImage {
				image {
					altText
					gatsbyImageData(aspectRatio: 1, width: 640)
				}
			}
		}
    priceRangeV2 {
      minVariantPrice {
        amount
        currencyCode
      }
    }
    vendor
  }
`;
